import { useState, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronRight } from 'lucide-react';

type Props = {
  title: string;
  /** Right-aligned metadata in the header (e.g. row count). */
  meta?: ReactNode;
  /** Whether the section starts expanded. Defaults to true. */
  defaultOpen?: boolean;
  /** Optional Lucide icon before the title. */
  icon?: ReactNode;
  /** Inline content shown inside the section when open. */
  children: ReactNode;
};

export function CollapsibleSection({
  title,
  meta,
  defaultOpen = true,
  icon,
  children,
}: Props) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="border border-border rounded-lg bg-surface-1 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center gap-3 px-5 py-3.5 text-left transition-colors hover:bg-surface-2"
      >
        <motion.span
          animate={{ rotate: open ? 90 : 0 }}
          transition={{ duration: 0.2 }}
          className="text-neutral-500 shrink-0"
        >
          <ChevronRight size={14} />
        </motion.span>
        {icon ? <span className="text-neutral-500 shrink-0">{icon}</span> : null}
        <span className="text-[11px] uppercase tracking-widest text-neutral-500 flex-1">
          {title}
        </span>
        {meta ? <span className="text-xs text-neutral-500 shrink-0">{meta}</span> : null}
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            style={{ overflow: 'hidden' }}
          >
            <div className="px-5 pb-5 pt-1">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
